import posthog from "posthog-js";

/**
 * PostHog analytics adapter.
 *
 * Conforms to AnalyticsAdapter (see src/analytics/contract.js).
 * This is the ONLY file that imports posthog-js. The eslint
 * no-restricted-imports rule enforces this boundary.
 */

let initialized = false;

const posthogAdapter = {
  name: "posthog",

  init: async (apiKey, opts = {}) => {
    if (!apiKey) return;
    posthog.init(apiKey, {
      api_host: opts.host || "https://us.i.posthog.com",
      // When api_host is a reverse proxy (e.g. d.groupardy.com), ui_host keeps
      // PostHog toolbar / "view in PostHog" links pointed at the real app.
      ui_host: "https://us.posthog.com",
      // We fire page views manually via AnalyticsProvider on route changes.
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: false,
      // Native exception autocapture. Registered at init() — before React
      // renders and before Clerk/Firebase load — so startup failures are
      // captured too. capture_console_errors turns library failures that only
      // log to console.error (e.g. a Clerk widget that fails to mount) into
      // $exception events instead of vanishing. This supersedes the manual
      // window listeners that used to live in ErrorBoundary.
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: true,
      },
      // Surface console logs inside session replays for diagnosis.
      enable_recording_console_log: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-private]",
        blockSelector: "[data-analytics-block]",
      },
      loaded: () => {
        initialized = true;
      },
    });

    // Register super properties sent with every event.
    posthog.register({
      environment: opts.env || "unknown",
    });
  },

  identify: (userId, traits = {}) => {
    if (!initialized) return;
    posthog.identify(userId, traits);
  },

  alias: (previousId, newId) => {
    if (!initialized) return;
    // PostHog signature: alias(alias, original)
    // alias = the new identity, original = the old one
    posthog.alias(newId, previousId);
  },

  track: (eventName, props = {}) => {
    if (!initialized) return;
    posthog.capture(eventName, props);
  },

  page: (path, props = {}) => {
    if (!initialized) return;
    posthog.capture("$pageview", { ...props, $current_url: path });
  },

  captureError: (error, ctx = {}) => {
    if (!initialized) return;
    posthog.captureException(error, ctx);
  },

  reset: () => {
    if (!initialized) return;
    posthog.reset();
  },

  setOptOut: (optedOut) => {
    if (!initialized) return;
    if (optedOut) {
      posthog.opt_out_capturing();
    } else {
      posthog.opt_in_capturing();
    }
  },
};

export default posthogAdapter;
